import { createSignal, onCleanup, Show } from "solid-js";
import { BotState } from "../models/BotState";
import { useToast } from "./MessageBox";
import Text from "./Text";
import { useTranslation } from "../hooks/useTranslation";
import styles from "./AddonState.module.css";
import { Citybuild } from "../models/Citybuild";

interface RegistryEntry {
    source: EventSource;
    refCount: number;
}

const eventSourceRegistry = new Map<string, RegistryEntry>();

function acquireEventSource(key: string): EventSource {
    const existing = eventSourceRegistry.get(key);
    if (existing) {
        existing.refCount++;
        return existing.source;
    }
    const source = new EventSource("/api/WallE/state" + (key ? `/${key}` : ""));
    eventSourceRegistry.set(key, { source, refCount: 1 });
    return source;
}

function releaseEventSource(key: string): void {
    const entry = eventSourceRegistry.get(key);
    if (!entry) return;
    entry.refCount--;
    if (entry.refCount <= 0) {
        entry.source.close();
        eventSourceRegistry.delete(key);
    }
}

export default function AddonState({ id }: { id?: string }) {
    const toast = useToast();
    const { t } = useTranslation();
    const key = id ?? "";

    const [state, setState] = createSignal<BotState>({
        citybuild: undefined,
        isConnected: false,
        isOnline: false,
        serverName: undefined,
        currentTaskName: undefined,
        currentTaskDescription: undefined,
        currentTaskProgress: 0
    });

    const eventSource = acquireEventSource(key);

    const onMessage = (event: MessageEvent) => {
        setState(JSON.parse(event.data));
    };
    const onError = (_error: Event) => {
        // SSE error handled by reconnection logic
    };
    const onEnd = () => {
        toast?.warning(t('ADDONSTATE_RECONNECTING'));
        setState((prevState) => ({ ...prevState, isOnline: false }));
    };

    eventSource.addEventListener("message", onMessage);
    eventSource.addEventListener("error", onError);
    eventSource.addEventListener("end", onEnd);

    onCleanup(() => {
        eventSource.removeEventListener("message", onMessage);
        eventSource.removeEventListener("error", onError);
        eventSource.removeEventListener("end", onEnd);
        releaseEventSource(key);
    });

    const hasActiveTask = () => {
        const taskName = state().currentTaskName;
        return taskName && taskName.trim() !== '';
    };

    return (
        <div class={styles.widget}>
            <div class={styles.header}>
                <h3 class={styles.title}>
                    <i class="fa-solid fa-robot" style="margin-right: 8px;"></i>
                    <Text val="ADDONSTATE_TITLE" />
                </h3>
                <div class={`${styles.statusIndicator} ${state().isOnline ? styles.online : styles.offline}`}></div>
            </div>

            <div class={styles.content}>
                <div class={styles.statusRow}>
                    <span class={styles.statusLabel}>
                        <i class={`fa-solid fa-power-off ${styles.icon}`}></i>
                        <Text val="ADDONSTATE_TITLE" />
                    </span>
                    <span class={`${styles.statusValue} ${state().isOnline ? styles.connected : styles.disconnected}`}>
                        <Show when={state().isOnline} fallback={
                            <>
                                <i class="fa-solid fa-circle-xmark"></i>
                                <Text val="ADDONSTATE_OFFLINE" />
                            </>
                        }>
                            <i class="fa-solid fa-circle-check"></i>
                            <Text val="ADDONSTATE_ONLINE" />
                        </Show>
                    </span>
                </div>

                <Show when={state().isOnline}>
                    <div class={styles.statusRow}>
                        <span class={styles.statusLabel}>
                            <i class={`fa-solid fa-link ${styles.icon}`}></i>
                            <Text val="ADDONSTATE_CONNECTION" />
                        </span>
                        <span class={`${styles.statusValue} ${state().isConnected ? styles.connected : styles.disconnected}`}>
                            <Show when={state().isConnected} fallback={
                                <>
                                    <i class="fa-solid fa-unlink"></i>
                                    <Text val="ADDONSTATE_DISCONNECTED" />
                                </>
                            }>
                                <i class="fa-solid fa-link"></i>
                                <Text val="ADDONSTATE_CONNECTED" />
                            </Show>
                        </span>
                    </div>
                </Show>

                <Show when={state().isOnline && state().isConnected && state().serverName}>
                    <div class={styles.serverInfo}>
                        <i class="fa-solid fa-server" style="margin-right: 6px;"></i>
                        {state().serverName}
                    </div>
                </Show>

                <Show when={state().isOnline && state().citybuild !== undefined}>
                    <div class={styles.citybuildInfo}>
                        <i class="fa-solid fa-building" style="margin-right: 6px;"></i>
                        {Citybuild[state().citybuild ?? Citybuild.CB1]}
                    </div>
                </Show>

                <Show when={state().isOnline}>
                    <Show when={hasActiveTask()} fallback={
                        <div class={styles.noTask}>
                            <i class="fa-solid fa-pause-circle" style="margin-right: 6px;"></i>
                            <Text val="ADDONSTATE_NO_TASK" />
                        </div>
                    }>
                        <div class={styles.taskSection}>
                            <div class={styles.taskHeader}>
                                <span class={styles.taskName}>
                                    <i class="fa-solid fa-tasks" style="margin-right: 6px;"></i>
                                    {state().currentTaskName}
                                </span>
                                <span class={`${styles.taskProgress} ${(state().currentTaskProgress || 0) === -1 ? 'task-failed' : ''}`} style={(state().currentTaskProgress || 0) === -1 ? 'background-color: #ef4444; color: white;' : ''}>
                                    {(state().currentTaskProgress || 0) === -1 ? t('ADDONSTATE_FAILED') : `${Math.round(state().currentTaskProgress || 0)}%`}
                                </span>
                            </div>
                            
                            <Show when={state().currentTaskDescription}>
                                <div class={styles.taskDescription}>
                                    {state().currentTaskDescription}
                                </div>
                            </Show>

                            <div class={styles.progressBar}>
                                <div 
                                    class={`${styles.progressFill} ${(state().currentTaskProgress || 0) > 0 && (state().currentTaskProgress || 0) < 100 && (state().currentTaskProgress || 0) !== -1 ? styles.active : ''}`}
                                    style={`width: ${(state().currentTaskProgress || 0) === -1 ? '100%' : `${state().currentTaskProgress || 0}%`}; background: ${(state().currentTaskProgress || 0) === -1 ? '#ef4444' : 'linear-gradient(90deg, #8b5cf6, #a78bfa)'};`}
                                ></div>
                            </div>
                        </div>
                    </Show>
                </Show>
            </div>
        </div>
    );
};